import type { ClubDetailDto, UpdateClubInput } from '@rw/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateClubs } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { toast } from '../../stores/toasts';
import { t } from '../../i18n';

/** All club mutations share error toasts and cache invalidation. */
export function useClubAction<TVars, TResult = unknown>(fn: (v: TVars) => Promise<TResult>, success?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      invalidateClubs(qc);
      if (success) toast({ kind: 'success', title: t(success) });
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : t('Something went wrong') }),
  });
}

export const clubApi = {
  create: (body: { name: string; description: string }) => api<ClubDetailDto>('/clubs', { body }),
  update: (clubId: string, body: UpdateClubInput) => api<ClubDetailDto>(`/clubs/${clubId}`, { method: 'PATCH', body }),
  setLogo: (clubId: string, image: string) => api<ClubDetailDto>(`/clubs/${clubId}/logo`, { method: 'PUT', body: { image } }),
  removeLogo: (clubId: string) => api<ClubDetailDto>(`/clubs/${clubId}/logo`, { method: 'DELETE' }),
  requestJoin: (clubId: string) => api(`/clubs/${clubId}/join-requests`, { method: 'POST' }),
  cancelRequest: (id: string) => api(`/club-requests/${id}/cancel`, { method: 'POST' }),
  approve: (id: string) => api(`/club-requests/${id}/approve`, { method: 'POST' }),
  decline: (id: string) => api(`/club-requests/${id}/decline`, { method: 'POST' }),
  acceptInvite: (id: string) => api(`/club-invites/${id}/accept`, { method: 'POST' }),
  declineInvite: (id: string) => api(`/club-invites/${id}/decline`, { method: 'POST' }),
  invite: (clubId: string, userId: string) => api(`/clubs/${clubId}/invites`, { body: { userId } }),
  leave: () => api('/me/club/leave', { method: 'POST' }),
  remove: (clubId: string, userId: string) => api(`/clubs/${clubId}/members/${userId}`, { method: 'DELETE' }),
  transfer: (clubId: string, userId: string) => api(`/clubs/${clubId}/transfer`, { body: { userId } }),
  resetCode: (clubId: string) => api(`/clubs/${clubId}/invite-code/reset`, { method: 'POST' }),
  joinByCode: (code: string) => api<{ id: string }>('/clubs/join-by-code', { body: { code } }),
};
